import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type ParkExpenseResource,
  ParkExpensesController_record_v1,
  type ParkExpensesController_record_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatPaise, paiseFromRupees, rupeesFromPaise } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { DayField } from '../billing-machines/machine-fields';
import { amountSchema, reasonSchema } from '../reporting/expense-form';
import { currentBusinessDate } from '../reporting/reporting-period';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * Spending that belongs to the park rather than any counter — diesel, petrol,
 * a parcel charge, an advance: `POST /v1/park-expenses`, owner or manager.
 *
 * It comes off the day's sheet in its own block and appears **nowhere else** —
 * not the P&L, the trading summary or the expenses list. The copy here says
 * nothing about why; whether diesel should reach the P&L is the owner's open
 * question, and this screen does not answer it for them.
 *
 * **A line is never edited.** A correction is a new line naming the live one in
 * `correctsParkExpenseId`, on the same day; removing a line is the same
 * correction with `amountPaise: 0`. A zero on a *new* line is refused, which is
 * why the amount field takes the expense form's fragment — at least a paisa —
 * and removal is its own button rather than a typed zero.
 */

export type ParkExpenseEdit =
  | { readonly kind: 'new'; readonly businessDate: string }
  | { readonly kind: 'correct'; readonly line: ParkExpenseResource };

const DAY_REQUIRED = 'Choose the day the money left.';

const parkExpenseSchema = z.object({
  businessDate: z.string().min(1, DAY_REQUIRED),
  reason: reasonSchema,
  amount: amountSchema,
});

type ParkExpenseValues = z.infer<typeof parkExpenseSchema>;

export function ParkExpenseDialog({
  edit,
  onClose,
  onRecorded,
  onStale,
}: {
  edit: ParkExpenseEdit;
  onClose: () => void;
  onRecorded: (line: ParkExpenseResource) => void;
  /** The line on screen was already corrected: the day must be read again. */
  onStale: () => void;
}) {
  const client = useApiClient();
  const today = currentBusinessDate(systemClock);
  const line = edit.kind === 'correct' ? edit.line : null;

  const form = useForm({
    resolver: zodResolver(parkExpenseSchema),
    mode: 'onTouched',
    defaultValues:
      line === null
        ? { businessDate: edit.kind === 'new' ? edit.businessDate : today, reason: '', amount: '' }
        : {
            businessDate: line.businessDate,
            reason: line.reason,
            amount: rupeesFromPaise(line.amountPaise),
          },
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const attempt = useRef<WriteAttempt | null>(null);

  const pending = form.formState.isSubmitting || removing;

  const send = async (body: ParkExpensesController_record_v1Body) => {
    setFormError(null);

    const sending = attemptFor(attempt.current, JSON.stringify(body));
    attempt.current = sending;

    try {
      onRecorded(
        await client.request(ParkExpensesController_record_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'REPORTING_PARK_EXPENSE_SUPERSEDED') {
        onStale();
      }

      setFormError(messageForFailure(error));
    }
  };

  const handleSubmit = async (values: ParkExpenseValues) => {
    if (pending) return;

    await send({
      // A correction stays on its line's day; the API refuses any other.
      businessDate: line?.businessDate ?? values.businessDate,
      amountPaise: paiseFromRupees(values.amount),
      // Sent exactly as typed — the record cannot be edited afterwards.
      reason: values.reason,
      ...(line === null ? {} : { correctsParkExpenseId: line.id }),
    });
  };

  const remove = async () => {
    if (line === null || pending) return;

    setRemoving(true);
    await send({
      businessDate: line.businessDate,
      amountPaise: 0,
      reason: line.reason,
      correctsParkExpenseId: line.id,
    });
    setRemoving(false);
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>
            {line === null ? 'Record a park expense' : 'Correct park expense'}
          </DialogTitle>
          {line !== null && (
            <DialogDescription>Replaces the line below. Both are kept.</DialogDescription>
          )}
        </DialogHeader>

        {line !== null && (
          <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground numeric">
            {formatBusinessDate(line.businessDate)} — {line.reason}, {formatPaise(line.amountPaise)}{' '}
            now
          </p>
        )}

        <Form {...form}>
          <form
            className="space-y-5"
            noValidate
            onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
          >
            {line === null && (
              <DayField
                control={form.control}
                name="businessDate"
                label="Day"
                maxDay={today}
                idPrefix="park-expense"
              />
            )}

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Reason
                  </FormLabel>
                  <FormControl
                    render={
                      <Input
                        className={`${fieldSurface} h-11`}
                        required
                        placeholder="Diesel for generator"
                        autoComplete="off"
                        {...field}
                      />
                    }
                  />
                  <FormDescription>As the book writes it.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Amount, in rupees
                  </FormLabel>
                  <FormControl
                    render={
                      <Input
                        className={`${fieldSurface} h-11 font-mono numeric`}
                        required
                        inputMode="decimal"
                        placeholder="3300.00"
                        autoComplete="off"
                        {...field}
                      />
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {formError !== null && (
              <Alert variant="destructive">
                <IconAlertTriangle />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              {line !== null && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => void remove()}
                >
                  {removing ? 'Removing…' : 'Remove this line'}
                </Button>
              )}
              <Button type="submit" disabled={pending}>
                {form.formState.isSubmitting
                  ? 'Saving…'
                  : line === null
                    ? 'Record'
                    : 'Record correction'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </FormDialogContent>
    </Dialog>
  );
}
